import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ticket, Copy, ExternalLink, Search, Loader2, Check, Plus, Info, Gift } from "lucide-react";
import { toast } from "sonner";
import { getCoupons } from "@/lib/api";
import { EmptyState } from "@/components/Skeletons";
import { BackButton } from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";
import { CouponDetails, ExpiryBadge } from "@/components/CouponDetails";
import { useCouponOfDay, storeName } from "@/components/CouponOfDay";

const PAGE = 24;
const TABS = [
  { key: "codes", label: "Codici sconto" },
  { key: "offers", label: "Offerte" },
];

export default function Coupon() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [copied, setCopied] = useState(null);
  const [tab, setTab] = useState("codes");
  const [detail, setDetail] = useState(null);
  const cod = useCouponOfDay();

  useEffect(() => {
    document.title = "Coupon e codici sconto · Readoo";
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    let alive = true;
    getCoupons()
      .then((r) => { if (alive) setCoupons(r.coupons || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byTab = coupons.filter((c) => c.promotion_id !== cod?.promotion_id && (tab === "codes" ? !!c.voucher_code : !c.voucher_code));
    if (!q) return byTab;
    return byTab.filter((c) =>
      [c.advertiser_name, c.title, c.description, c.voucher_code].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [coupons, query, tab, cod]);

  const counts = useMemo(() => {
    const list = coupons.filter((c) => c.promotion_id !== cod?.promotion_id);
    const codes = list.filter((c) => c.voucher_code).length;
    return { codes, offers: list.length - codes };
  }, [coupons, cod]);

  const copyCode = async (code, id) => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(code);
      ok = true;
    } catch {
      const host = document.activeElement?.closest?.('[role="dialog"]') || document.body;
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.setAttribute("readonly", "");
      ta.style.cssText = "position:fixed;opacity:0;top:0;left:0";
      host.appendChild(ta);
      ta.select();
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      host.removeChild(ta);
    }
    if (!ok) return toast.error("Impossibile copiare il codice");
    setCopied(id);
    toast.success("Codice copiato!");
    setTimeout(() => setCopied((p) => (p === id ? null : p)), 2000);
  };

  if (authLoading || !user) return null;

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <BackButton />
      <div className="flex items-center gap-3 mt-4 mb-2">
        <span className="w-11 h-11 rounded-full bg-brandTertiary flex items-center justify-center shrink-0">
          <Ticket size={20} className="text-brand" />
        </span>
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-onSurface leading-none">Coupon e codici sconto</h1>
          <p className="text-sm text-subtle mt-1.5">Offerte e codici dei nostri partner. Approfittane prima che scadano.</p>
        </div>
      </div>

      {cod && <CodHero offer={cod} onCopy={copyCode} copied={copied} onDetails={() => setDetail(cod)} />}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 mb-6">
      <div role="tablist" className="flex gap-2" data-testid="coupon-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => { setTab(t.key); setShown(PAGE); }}
            data-testid={`coupon-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${tab === t.key ? "bg-brand text-onBrand border-brand" : "bg-surfaceSecondary text-onSurface border-hair hover:border-brand/50 hover:text-brand"}`}
          >
            {t.label}
            {!loading && <span data-testid={`coupon-tab-count-${t.key}`} className="font-mono text-[11px] opacity-80">{counts[t.key]}</span>}
          </button>
        ))}
      </div>

      <div className="relative w-full sm:w-80 sm:ml-auto">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-subtle" />
        <input
          data-testid="coupon-search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShown(PAGE); }}
          placeholder="Cerca per negozio o offerta…"
          className="w-full bg-surfaceSecondary border border-hair rounded-full pl-10 pr-4 py-2.5 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand transition-colors"
        />
      </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Ticket} title={tab === "codes" ? "Nessun codice sconto disponibile" : "Nessuna offerta disponibile"} subtitle="Torna più tardi: aggiorniamo le offerte di continuo." />
      ) : (
        <>
          <div data-testid="coupon-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.slice(0, shown).map((c) => (
              <div key={c.promotion_id} data-testid={`coupon-${c.promotion_id}`} className="flex flex-col rounded-2xl border border-hair bg-surfaceSecondary p-4 hover:border-brand/40 transition-colors">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand truncate">{c.advertiser_name}</span>
                  {c.voucher_code ? (
                    <span className="font-mono text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-brandTertiary text-brand shrink-0">Codice</span>
                  ) : (
                    <span className="font-mono text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-surfaceTertiary text-onSurfaceSecondary shrink-0">Offerta</span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-onSurface leading-snug line-clamp-3">{c.title}</h3>
                {c.description && <p className="text-xs text-subtle mt-1.5 line-clamp-3">{c.description}</p>}
                <div className="flex items-center justify-between gap-2 mt-2.5">
                  <ExpiryBadge end={c.end_date} id={`coupon-expiry-${c.promotion_id}`} />
                  <button
                    type="button"
                    onClick={() => setDetail(c)}
                    data-testid={`coupon-details-btn-${c.promotion_id}`}
                    className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline shrink-0"
                  >
                    <Info size={13} /> Dettagli
                  </button>
                </div>
                <div className="mt-auto pt-4 space-y-2">
                  {c.voucher_code && (
                    <button
                      type="button"
                      onClick={() => copyCode(c.voucher_code, c.promotion_id)}
                      data-testid={`coupon-copy-${c.promotion_id}`}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-dashed border-brand/50 bg-brandTertiary/40 text-brand font-mono text-sm font-bold hover:bg-brandTertiary transition-colors"
                    >
                      <span className="truncate">{c.voucher_code}</span>
                      {copied === c.promotion_id ? <Check size={15} /> : <Copy size={15} />}
                    </button>
                  )}
                  {c.tracking_url && (
                    <a
                      href={c.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      data-testid={`coupon-link-${c.promotion_id}`}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors"
                    >
                      Vai all'offerta <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          {shown < filtered.length && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => setShown((s) => s + PAGE)}
                data-testid="coupon-load-more"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-hair bg-surfaceSecondary text-onSurface hover:border-brand/50 hover:text-brand transition-colors text-sm font-semibold"
              >
                <Plus size={16} /> Mostra altri ({filtered.length - shown})
              </button>
            </div>
          )}
        </>
      )}
      <CouponDetails coupon={detail} onClose={() => setDetail(null)} onCopy={copyCode} copied={copied} />
    </div>
  );
}

function CodHero({ offer: o, onCopy, copied, onDetails }) {
  return (
    <section id="offerta-del-giorno" data-testid="coupon-of-day-hero" className="relative overflow-hidden mt-6 rounded-2xl border-2 border-brand/40 bg-brandTertiary/30 p-5 sm:p-7">
      <span aria-hidden className="absolute -right-8 -top-8 w-36 h-36 rounded-full bg-brand/10" />
      <div className="relative flex flex-col md:flex-row md:items-center gap-5">
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand text-onBrand font-mono text-[10px] font-bold uppercase tracking-wider mb-3">
            <Gift size={12} /> Offerta del giorno
          </span>
          <p className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand mb-1">{storeName(o.advertiser_name)}</p>
          <h2 data-testid="coupon-of-day-headline" className="font-serif text-2xl sm:text-3xl font-semibold leading-tight text-onSurface">{o.headline}</h2>
          {o.description && <p className="text-sm text-onSurfaceSecondary mt-2 line-clamp-2">{o.description}</p>}
          <div className="flex items-center gap-3 mt-3">
            <ExpiryBadge end={o.end_date} id="coupon-of-day-expiry" />
            <button type="button" onClick={onDetails} data-testid="coupon-of-day-details" className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline">
              <Info size={13} /> Dettagli
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-2.5 md:w-64 shrink-0">
          <button type="button" onClick={() => onCopy(o.voucher_code, o.promotion_id)} data-testid="coupon-of-day-copy"
            className="flex items-center justify-between gap-2 px-3 py-3 rounded-lg border border-dashed border-brand/60 bg-surface/60 text-brand font-mono text-base font-bold tracking-wider hover:bg-brandTertiary transition-colors">
            <span className="truncate">{o.voucher_code}</span>
            {copied === o.promotion_id ? <Check size={16} /> : <Copy size={16} />}
          </button>
          {o.tracking_url && (
            <a href={o.tracking_url} target="_blank" rel="noopener noreferrer sponsored" data-testid="coupon-of-day-link"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg bg-brand text-onBrand text-sm font-bold hover:bg-brandSecondary transition-colors">
              Vai all'offerta <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
